import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { downloadPDF } from "../../utils/pdf";
import { formatINR, formatDate } from "../../utils/helpers";
import { CheckCircle, XCircle, Clock, Plus, X, Download, Trash2, AlertTriangle } from "lucide-react";

export default function Loans() {
  const { loanRequests, requestLoan, approveLoan, denyLoan, deleteLoan, mode, currentUserId, team , repayLoan} = useApp();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount:"", reason:"", emi:"" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDeleteLoan = (id) => {
    if (confirmDelete === id) { deleteLoan(id); setConfirmDelete(null); }
    else { setConfirmDelete(id); setTimeout(() => setConfirmDelete(null), 3000); }
  };

  useEffect(() => { const h = () => setShowForm(false); window.addEventListener("closeModals", h); return () => window.removeEventListener("closeModals", h); }, []);

  const pending = loanRequests.filter(l => l.status === "Pending");
  const processed = loanRequests.filter(l => l.status !== "Pending");
  const myLoans = loanRequests.filter(l => l.employeeId === currentUserId);
  const emp = team.find(m => m.id === currentUserId);

  const handleSubmit = (e) => { e.preventDefault(); if (requestLoan(form)) { setShowForm(false); setForm({ amount:"", reason:"", emi:"" }); } };

  
  const exportLoansPDF = () => {
    downloadPDF("Loan Requests Report",
      ["Employee", "Amount", "EMI", "Reason", "Status", "Date"],
      loanRequests.map(l => [l.employee, formatINR(l.amount), formatINR(l.emi) + "/mo", l.reason || "—", l.status, formatDate(l.createdAt)]),
      [
        { label: "Total Requests", value: loanRequests.length + "" },
        { label: "Pending", value: loanRequests.filter(l => l.status === "Pending").length + "" },
        { label: "Total Amount", value: formatINR(loanRequests.reduce((s,l) => s + Number(l.amount), 0)), color: "#F97316" },
      ]
    );
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h3>Loan Requests</h3><span className="text-muted">{mode==="admin" ? pending.length+" pending | "+processed.length+" processed" : myLoans.length+" requests"}</span><button className="btn-ghost" onClick={exportLoansPDF} style={{display:"flex",alignItems:"center",gap:6}}><Download size={14}/> PDF</button>
      </div>
      {mode === "employee" && <button className="btn-gold" onClick={() => setShowForm(true)}><Plus size={16}/> Request Loan</button>}
      </div>

      {mode==="admin" && pending.length > 0 && (
        <div className="card" style={{marginBottom:20}}>
          <h3><Clock size={16} style={{display:"inline",verticalAlign:"middle",marginRight:8,color:"#F97316"}}/>Pending Approval</h3>
          {pending.map(l => (
            <div key={l.id} className="loan-row">
              <div className="loan-info-block"><div className="primary">{l.employee}</div><div className="text-muted">{l.reason} | {formatDate(l.createdAt)}</div></div>
              <div className="loan-amount-block"><div className="mono amount">{formatINR(l.amount)}</div><div className="text-muted">EMI: {formatINR(l.emi)}/mo</div></div>
              <div className="loan-actions">
                <button className="btn-approve" onClick={() => approveLoan(l.id)}><CheckCircle size={16}/> Approve</button>
                <button className="btn-deny" onClick={() => denyLoan(l.id)}><XCircle size={16}/> Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="table-card">
        <table>
          <thead><tr><th>Employee</th><th>Amount</th><th>Reason</th><th>Status</th><th>Date</th><th>REPAYMENT</th>{mode==="admin"&&<th>Actions</th>}</tr></thead>
          <tbody>
            {(mode==="admin" ? loanRequests : myLoans).map(l => (
              <tr key={l.id}>
                <td className="primary">{l.employee}</td>
                <td className="mono amount">{formatINR(l.amount)}</td>
                <td className="text-muted">{l.reason}</td>
                
                <td><span className={"status-badge "+(l.status==="Approved"?"active":l.status==="Pending"?"pending":"inactive")}>{l.status}</span></td>
                <td className="mono">{formatDate(l.createdAt)}</td>
                    <td>{l.repaid ? <span style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:700,background:"rgba(16,185,129,.15)",color:"#10B981",letterSpacing:0.5}}>Loan Paid ✓</span> : l.status === "Approved" ? <span style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:700,background:"rgba(245,166,35,.15)",color:"#F5A623"}}>Repay Pending</span> : <span style={{fontSize:11,color:"#475569"}}>—</span>}</td>
                {mode==="admin"&&<td className="actions-cell">{confirmDelete===l.id ? (
                  <button className="btn-deny" style={{padding:"5px 12px",fontSize:11}} onClick={()=>handleDeleteLoan(l.id)}><AlertTriangle size={13}/> Delete?</button>
                ) : (
                  <button className="icon-btn danger" title="Delete loan" onClick={()=>handleDeleteLoan(l.id)}><Trash2 size={14}/></button>
                )}</td>}
              </tr>
            ))}
            {(mode==="admin"?loanRequests:myLoans).length===0 && <tr><td colSpan={mode==="admin"?7:6} className="empty-row">No loan requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Request Loan</h3><button className="icon-btn" onClick={() => setShowForm(false)}><X size={18}/></button></div>
            {emp && <div style={{fontSize:12,color:"var(--t4)",marginBottom:16}}>Max loan: {formatINR(emp.salary * 3)} (3x salary)</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Amount</label><input type="number" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} placeholder="Loan amount" min="1" required/></div>
              <div className="form-group"><label>Reason</label><input type="text" value={form.reason} onChange={e => setForm({...form, reason:e.target.value})} placeholder="Why do you need this loan?" maxLength={200} required/></div>
              <button type="submit" className="btn-gold full">Submit Request</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
